import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLive } from "@/contexts/LiveContext";
import { UserAvatar } from "@/components/Navbar";
import { Bell, Heart, MessageCircle, Check, Flame } from "lucide-react";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function NotificationBell() {
  const { user, api } = useAuth();
  const { subscribe } = useLive();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  }, [api, user]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  // Real-time notifications via shared socket
  useEffect(() => {
    if (!user) return;
    return subscribe((msg) => {
      if (msg.type === "notification" && msg.user_id === user.user_id) {
        setItems((prev) => [msg.notification, ...prev]);
        setUnread((u) => u + 1);
      }
    });
  }, [user, subscribe]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Allow the mobile bottom-nav "Activity" tab to open this panel
  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener("fv-open-notifications", openPanel);
    return () => window.removeEventListener("fv-open-notifications", openPanel);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch {}
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch {}
      setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, is_read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    navigate(n.topic_id ? `/issue/${n.topic_id}` : "/");
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-neutral hover:text-dark transition-colors rounded-lg hover:bg-gray-50"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-view-b rounded-full" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-4 right-4 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-card-hover border border-gray-100 overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-heading text-base text-dark tracking-wide">ACTIVITY</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-[11px] font-semibold text-view-a hover:text-view-a-dark transition-colors" data-testid="mark-all-read-btn">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-neutral">No activity yet.</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => handleClick(n)}
                  className={`w-full flex gap-3 px-4 py-3 text-left border-b border-gray-50 transition-colors hover:bg-gray-50 ${!n.is_read ? "bg-view-a-light/40" : ""}`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="relative shrink-0">
                    {n.type === "streak_reminder" ? (
                      <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#ea580c,#f59e0b)" }}>
                        <Flame className="w-4 h-4 text-white" fill="currentColor" />
                      </span>
                    ) : (
                      <>
                        <UserAvatar user={{ name: n.actor_name, avatar: n.actor_avatar }} size="w-8 h-8" textSize="text-[10px]" />
                        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ${n.type === "like" || n.type === "reaction" ? "bg-view-b" : "bg-view-a"}`}>
                          {n.type === "like" || n.type === "reaction"
                            ? <Heart className="w-2.5 h-2.5 text-white" fill="currentColor" />
                            : <MessageCircle className="w-2.5 h-2.5 text-white" />}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {n.type === "streak_reminder" ? (
                      <p className="text-sm text-dark leading-snug" data-testid="streak-reminder-text">
                        <span className="font-semibold">Streak at risk</span> — {n.preview}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-dark leading-snug">
                          <span className="font-semibold">{n.actor_name}</span>{" "}
                          {n.type === "like" ? "liked your comment" : n.type === "reaction" ? "reacted to your comment" : "replied to your comment"}
                        </p>
                        {n.preview && <p className="text-xs text-neutral truncate mt-0.5">"{n.preview}"</p>}
                      </>
                    )}
                    <p className="text-[11px] text-neutral mt-0.5">{timeAgo(n.created_at)}{n.topic_question ? ` · ${n.topic_question}` : ""}</p>
                  </div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-view-a shrink-0 mt-1.5" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
